import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ordersService } from '@/services';
import { Button } from '@/components/ui';

export function OrderConfirmationPage(): JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const { data: order, isLoading } = useQuery({
    queryKey: ['orders', orderId, 'confirmation'],
    queryFn: () => ordersService.getOrderById(orderId!),
    enabled: Boolean(orderId),
  });

  const isPending = order?.status === 'PAGO_PENDIENTE';

  if (!orderId) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-brand-700">{t('common.error')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-4 font-heading text-heading-2xl text-brand-950">{t('common.orderConfirmation')}</h1>
      <div className="rounded-lg border border-brand-700/20 bg-surface p-8 text-center">
        {isPending ? (
          <p className="mb-4 font-sans text-lg font-medium text-amber-800">
            {t('common.paymentPendingNotice')}
          </p>
        ) : (
          <p className="mb-4 font-sans text-lg font-medium text-green-700">{t('common.paymentSuccess')}</p>
        )}
        <p className="mb-2 font-sans text-brand-700">
          {t('common.orderNumber', { orderId: orderId.slice(0, 8) })}
        </p>
        <p className="mb-6 font-sans text-sm text-brand-700">
          <Link to={`/orders/${orderId}`} className="text-brand-600 hover:text-brand-400 hover:underline">
            {t('orders.viewDetail')} →
          </Link>
        </p>
        <div className="flex flex-col flex-wrap justify-center gap-3 sm:flex-row">
          <Button type="button" onClick={() => navigate('/orders')}>
            {t('orders.viewMyOrders')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/products')}>
            {t('common.continueShopping')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/cart')}>
            {t('common.backToCart')}
          </Button>
        </div>
      </div>
    </div>
  );
}
